import { routingFetch } from "@app/data/helpers";

export type GeolocationSearchResult = {
  id: string;
  label: string;
  street: string;
  postal_code: string;
  city: string;
  latitude: number;
  longitude: number;
};

type StaticMapOptions = {
  longitude: number;
  latitude: number;
  label?: string;
};

const BASE_PATH = "/api/v2/geolocation";

export const buildServerURL = ({
  longitude,
  latitude,
  label,
}: StaticMapOptions) => {
  const params = new URLSearchParams();
  params.set("lat", latitude.toString());
  params.set("lon", longitude.toString());
  if (label && label.trim().length > 0) {
    params.set("label", label.trim());
  }
  return `${BASE_PATH}/static-map?${params.toString()}`;
};

type SearchOptions = {
  limit?: number;
  country?: string;
  proximity?: string | { lat: string; lon: string };
  signal?: AbortSignal;
  types?: string[];
  language?: string;
};

type SearchResponse = {
  results?: GeolocationSearchResult[];
  error?: string;
};

export const searchGeolocations = async (
  query: string,
  options: SearchOptions = {},
): Promise<GeolocationSearchResult[]> => {
  const trimmed = query.trim();
  if (!trimmed) {
    return [];
  }

  const params = new URLSearchParams();
  params.set("autocomplete", "true");
  params.set("query", trimmed);
  params.set("limit", "5");
  params.set("country", options.country ?? "CH");
  if (options.proximity) {
    params.set(
      "proximity",
      typeof options.proximity === "string"
        ? options.proximity
        : `${options.proximity.lon},${options.proximity.lat}`,
    );
  }
  if (options.types && options.types.length > 0) {
    params.set("types", options.types.join(","));
  }
  if (options.language) {
    params.set("language", options.language);
  }

  const response = await routingFetch(`${BASE_PATH}/search?${params.toString()}`, {
    signal: options.signal,
  });

  if (!response.ok) {
    const fallback = (await response
      .json()
      .catch(() => ({}))) as SearchResponse;
    throw new Error(fallback.error || "Failed to fetch address suggestions");
  }

  const payload = (await response.json()) as SearchResponse;
  if (!Array.isArray(payload.results)) {
    return [];
  }
  return payload.results;
};
